<script type="module" lang="ts" setup>
import { ref, onMounted, onUnmounted, inject, computed } from 'vue'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import Photoswipe from 'photoswipe'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import type { Props } from './types'
import TechnicalSpecs from '@/components/BrandedV2/TechnicalSpecs/index.vue'
import { investmentProjectKey } from '@/symbols/listing'
import 'photoswipe/style.css'
import { useAppChatStore } from '@/stores/appChatStore'
import './_index.scss'
import { minMaxByKey } from '@/utils/objects'
import { truncateText } from '@/utils/strings'
import { getReferralBonus } from '@/utils/referralBonus'

const appChatStore = useAppChatStore()

const props = withDefaults(defineProps<Props>(), {
  pictures: () => [],
  videos: () => [],
  webinars: () => [],
  tours: () => [],
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})

const emit = defineEmits(['opacityEvent'])

const { t, locale } = useI18n()

const bonusWithCurrency = computed(() => getReferralBonus(locale.value, true))
const { $useLhId } = useNuxtApp()
const mediaTypes = ref(['pictures'])
const lightbox = ref()
const imagesZise = ref([{ width: 0, height: 0 }])
const selectedTab = ref(props.currentTab)

const listing = inject(investmentProjectKey) as Record<string, any>

const initializeMediaTypes = () => {
  if (props.location.lat && props.location.lng) mediaTypes.value.push('location')
  if (props.videos.length > 0) mediaTypes.value.push('videos')
  if (props.tours.length > 0) mediaTypes.value.push('tours')
  if (props.webinars.length > 0) mediaTypes.value.push('webinars')
  mediaTypes.value.push('technical_specs')
}

initializeMediaTypes()

const isBigByPosition = (index: number, size: number) => ((index + 1) % size) === 0

const videoSrc = (video: string): string => video.replace('watch?v=', 'embed/') + '?wmode=transparent'

const resolveSize = () => {
  const elements = document.getElementsByClassName('pictures-class')
  if (elements) {
    emit('opacityEvent', true)
    imagesZise.value = Array.from(elements).map(element => ({
      width: (element as HTMLElement).offsetWidth * 3,
      height: (element as HTMLElement).offsetHeight * 3
    }))
  }
}

const handleReferAndEarnClick = () => {
  appChatStore.openChatWindow({
    screen: 'PDP',
    entryNameAlias: 'PDP_REFER_AND_EARN',
    startFlow: true,
    shouldCallBotForGreeting: true
  })
  props.handleCloseGallery()
}

const resolveImageDevice = (picture: string) => props.isMobile
  ? `${picture}?auto=format,compress,enhance&w=600`
  : `${picture}?auto=format,compress,enhance&w=1080`

const iframeHeight = (content: string[]) => content?.length > 1 ? 'h-350 md:h-[50svh]' : 'h-[70svh]'

const checkIfInView = () => {
  const container = document.querySelector('.gallery-elements-container')
  const elements = container?.children || []
  let closest = Infinity
  let closestElement = null

  Array.from(elements).forEach((element) => {
    const rect = element.getBoundingClientRect()
    const containerRect = container!.getBoundingClientRect()
    const adjustedLeft = rect.left - containerRect.left
    const adjustedRight = rect.right - containerRect.left

    if (adjustedRight > 0 && adjustedLeft < container!.clientWidth) {
      const distance = Math.abs(container!.clientWidth / 2 - (adjustedLeft + rect.width / 2))
      if (distance < closest) {
        closest = distance
        closestElement = element
      }
    }
  })

  selectedTab.value = closestElement?.id
  const tabs = document.getElementById(`tab-${selectedTab.value}`)
  if (tabs?.scrollIntoView) tabs.scrollIntoView({ inline: 'center' })
}

const goToCurrentTab = (tab: string) => {
  const currentElement = document.getElementById(tab)
  if (currentElement?.scrollIntoView) {
    currentElement.scrollIntoView({ inline: 'center' })
    selectedTab.value = currentElement.id
  }
}

const toggleIframePointerEvents = (event: Event, enable: boolean) => {
  (event.target as HTMLElement).style.pointerEvents = enable ? 'auto' : 'none'
}

const disableIframe = (event: Event) => {
  toggleIframePointerEvents(event, false)
}

const enableIframe = (event: Event) => {
  toggleIframePointerEvents(event, true)
}

const technicalSpecsImages = computed(() => {
  if (props.pictures.length > 0) {
    return props.pictures.slice(0, 3)
  }
  return []
})

const deliveryTimeLabel = computed(() => {
  const date = new Date(listing.ims?.delivered_at)
  return date.toLocaleString('es-ES', {
    month: 'long',
    year: 'numeric'
  }).replace(/^\w/, c => c.toUpperCase())
})

const areaLabel = computed(() => {
  const areaData = [
    { area: listing.ims?.max_area },
    { area: listing.ims?.min_area }
  ]

  let areaRange = ''
  const areaMinMax = minMaxByKey(areaData, 'area')

  if (areaMinMax) {
    areaRange = areaMinMax.isEqual ? `${areaMinMax.min}m²` : `${areaMinMax.min}m² - ${areaMinMax.max}m²`
  }

  return areaRange
})

const companyName = computed(() => {
  return truncateText(listing.raw_listing?.companies?.[0]?.company_name ?? '', 26)
})

onMounted(() => {
  lightbox.value = new PhotoSwipeLightbox({
    gallerySelector: '#slider-gallery',
    childSelector: 'a',
    bgOpacity: 0,
    initialZoomLevel: 'fit',
    secondaryZoomLevel: props.isMobile ? 0.5 : 1.4,
    maxZoomLevel: props.isMobile ? 1 : 3,
    pswpModule: Photoswipe,
    wheelToZoom: true,
    preload: [1, 2]
  })

  lightbox.value.init()
  lightbox.value.on('closingAnimationEnd', () => emit('opacityEvent', false))
  goToCurrentTab(selectedTab.value as string)
})

onUnmounted(() => {
  lightbox.value.destroy()
  lightbox.value = null
})
</script>

<script lang="ts">
export default {
  name: 'AppGalleryV2'
}
</script>

<template>
  <div class="app-gallery h-[76svh] lg:h-[80svh]">
    <div class="flex w-full justify-center roomie-tabs h-full">
      <div class="w-full lg:w-[62%] h-full">
        <div class="pd-gallery-button-container flex flex-row flex-wrap gap-8 px-16 lg:px-0 py-12 lg:py-lh-24">
          <RoomieButton
            v-for="(type) in mediaTypes"
            :id="`pd-gallery-${type}-button`"
            :key="type"
            :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'seleccionar', C: t(type)})"
            :class="['gap-lh-8',{ 'is-pressed': (type === selectedTab) }]"
            variant="secondary"
            type="button"
            @click="goToCurrentTab(type)">
            {{ t(type) }}
          </RoomieButton>
        </div>

        <div
          class="gallery-elements-container w-full flex gap-24 scrollbar-hidden bg-gray2-600 h-full"
          @scroll="checkIfInView">
          <div
            v-if="mediaTypes.includes('pictures')"
            id="pictures"
            class="bg-white snap-center w-full flex-[0_0_auto]">
            <div
              v-show="selectedTab === 'pictures'"
              id="slider-gallery"
              class="h-full grid md:grid-cols-2 lg:grid-cols-3 auto-rows-[20rem] gap-4 overflow-y-auto scrollbar-hidden">
              <div
                v-for="(picture, index) in pictures"
                :key="picture"
                class="relative overflow-hidden"
                :class="{
                  'md:col-span-1 md:row-span-1': !isBigByPosition(index, 3),
                  'md:col-span-2 md:row-span-2': isBigByPosition(index, 3),
                  'lg:col-span-1 lg:row-span-1': !isBigByPosition(index, 4),
                  'lg:col-span-3 lg:row-span-2': isBigByPosition(index, 4)
                }">
                <a
                  :id="`picture-${index}-${id}`"
                  v-lh-id="{...dataAttributes.dataLh, A: 'ver', C: 'fotos', I: index}"
                  :href="`${picture}?auto=compress,enhance,format`"
                  :data-pswp-width="imagesZise[index]?.width"
                  :data-pswp-height="imagesZise[index]?.height"
                  class="w-[calc(100%+60px)] h-full flex items-center absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"
                  rel="noreferrer"
                  @click="resolveSize">
                  <img
                    :src="resolveImageDevice(picture)"
                    :alt="`project ${index}`"
                    class="pictures-class w-full object-contain pointer-events-none">
                </a>
              </div>
            </div>

            <div
              ref="customPrevButton"
              class="my-custom-prev-button">
              prev
            </div>

            <div
              ref="customNextButton"
              class="my-custom-next-button">
              next
            </div>
          </div>

          <div
            v-if="mediaTypes.includes('location')"
            id="location"
            class="bg-white snap-center w-full flex-[0_0_auto]">
            <AppMapDynamic
              v-if="selectedTab === 'location'"
              :id="location.mapId"
              :lat="location.lat"
              :lng="location.lng"
              marker-icon="https://assets.lahaus.com/static/images/logos/Pin.png"
              styles="w-full h-[70svh]" />
          </div>

          <div
            v-if="mediaTypes.includes('videos')"
            id="videos"
            class="bg-white snap-center w-full flex-[0_0_auto]">
            <div
              v-if="selectedTab === 'videos'"
              class="w-full h-full grid auto-rows-max gap-16 px-8 overflow-y-auto scrollbar-hidden"
              @mousedown="disableIframe"
              @mouseup="enableIframe">
              <iframe
                v-for="(video, index) in videos"
                :id="`video-${index}`"
                :key="`video-${index}`"
                :src="videoSrc(video)"
                title="YouTube video player"
                class="w-full rounded-8"
                :class="iframeHeight(videos as string[])"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen />
            </div>
          </div>

          <div
            v-if="mediaTypes.includes('webinars')"
            id="webinars"
            class="bg-white snap-center w-full aspect-[3/1]">
            <div
              v-if="selectedTab === 'webinars'"
              class="iframe-container w-full h-full grid auto-rows-max gap-16 px-8 overflow-y-auto scrollbar-hidden"
              @mousedown="disableIframe"
              @mouseup="enableIframe">
              <iframe
                v-for="(webinar, index) in webinars"
                :id="`webinar-${index}`"
                :key="`webinar-${index}`"
                :src="videoSrc(webinar)"
                title="YouTube video player"
                class="w-full rounded-8"
                :class="iframeHeight(webinars as string[])"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen />
            </div>
          </div>

          <div
            v-if="mediaTypes.includes('tours')"
            id="tours"
            class="bg-white snap-center w-full aspect-[3/1]">
            <div
              v-if="selectedTab === 'tours'"
              class="w-[90svw] max-w-[93rem] h-full grid auto-rows-max gap-16 px-8 overflow-y-auto scrollbar-hidden"
              @mousedown="disableIframe"
              @mouseup="enableIframe">
              <iframe
                v-for="(tour, index) in tours"
                :id="`tour-${index}`"
                :key="`tour-${index}`"
                :src="tour"
                title="3d tour"
                class="app-gallery w-full rounded-8"
                :class="iframeHeight(tours as string[])"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen />
            </div>
          </div>

          <div
            v-if="mediaTypes.includes('technical_specs')"
            id="technical_specs"
            class="snap-center w-full flex-[0_0_auto]">
            <div
              v-show="selectedTab === 'technical_specs'"
              class="m-auto h-full w-full overflow-y-auto scrollbar-hidden">
              <div class="w-full lg:h-full flex justify-center mb-[12rem] lg:mb-16">
                <TechnicalSpecs
                  :area-range="areaLabel"
                  :city-name="listing.city_label"
                  :company-name="companyName"
                  :currency="listing.currency"
                  :data-attributes="dataAttributes"
                  :delivery-time="deliveryTimeLabel"
                  :images="technicalSpecsImages"
                  :neighborhood-name="listing.neighborhood_label"
                  :project-name="listing.name"
                  :project-price="listing.min_price" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="w-full flex justify-center p-lh-16 lg:p-0 lg:px-0 gap-16 fixed bottom-0 shadow-[4px_0px_15px_0px_rgba(0,0,0,0.25)] lg:h-[10svh] bg-white">
      <div class="w-full lg:w-[62%] flex flex-col lg:flex-row justify-between items-center">
        <p
          v-sanitize="t('referralText', { bonusWithCurrency })"
          class="body-2-m lg:body-1-m text-pine-600" />

        <RoomieButton
          id="pd-gallery-refer-and-earn-button"
          :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'referir', C: t('ctaText')})"
          type="button"
          class="w-full lg:w-fit"
          variant="primary"
          @click="handleReferAndEarnClick">
          <div
            class="w-full flex flex-row justify-center items-center gap-4">
            <p>
              {{ t('ctaText') }}
            </p>
          </div>
        </RoomieButton>
      </div>
    </div>
  </div>
</template>

<i18n src="./i18n.json" lang="json"></i18n>

<style lang="scss">
.lh-roomie-v2 {
  .pswp {
    &__button {
      &--arrow--next,
      &--arrow--prev {
        @apply w-50 h-50 mx-16;
        @apply bg-white rounded-full shadow-[0_0_10px_#0000004f];
        @apply grid place-content-center;
        @apply hover:bg-white;
      }

      &--arrow--next {
        & .pswp__icn {
          @apply fill-primary-600 top-10 -left-4 scale-[.6] rotate-180;
        }
      }

      &--arrow--prev {
        & .pswp__icn {
          @apply fill-primary-600 top-10 -left-0 scale-[.6];
        }
      }
    }

    &__icn-shadow {
      @apply hidden;
    }

    &__counter {
      @apply text-18 font-medium;
    }

    &__item {
      background-color: #121212cc;
    }
  }

  .app-gallery {
    .app-slider {
      @apply -mx-24;

      height: 90%;

      &-items-holder {
        @apply h-full;
      }

      &__button {
        display: flex;
      }

      @media screen and (max-width: theme('screens.lg') ) {
        &__button {
        @apply w-24 h-24;

          & span {
            @apply w-24 h-24;
          }
        }
      }

      iframe {
        scroll-snap-align: center;
      }
    }

    .pd-gallery-button-container {
      .is-pressed {
        @apply bg-pine-200;
      }

      button {
        @apply px-16 cta-14;

        height: 3.2rem;

        @media (width >= theme('screens.lg')) {
          @apply px-24 cta-16;

          height: 4.8rem;
        }
      }
    }
  }

  .gallery-elements-container {
    @apply overflow-y-hidden;

    scroll-snap-type: x mandatory;
    scroll-snap-stop: always;
  }
}
</style>
